/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
/**
 * `entities/analysis` TanStack Query key factory.
 *
 * Tuple shapes are pinned by `query-keys.test.ts` (cache contract).
 *
 * The root namespace is intentionally `['analyses']` and the detail key is
 * `['analysis', id]` — these reproduce the ad-hoc keys used by the legacy
 * `features/analysis` hooks so that the mutation hooks (which stay in
 * `features/analysis` and call `invalidateQueries({ queryKey: ['analyses'] })`)
 * keep invalidating the list after the read layer moves here.
 *
 * Surface:
 *   - `ANALYSIS_QUERY_KEYS.all`             → `['analyses']`
 *   - `ANALYSIS_QUERY_KEYS.lists()`         → `['analyses']`
 *   - `ANALYSIS_QUERY_KEYS.detail(id)`      → `['analysis', id]`
 *   - `ANALYSIS_QUERY_KEYS.resolution(id)`  → `['analysis', id, 'resolution']`
 *   - `ANALYSIS_QUERY_KEYS.statistaDatasets(id)`
 *                                           → `['analysis', id, 'statista-datasets']`
 */
export const ANALYSIS_QUERY_KEYS = {
  /** Root namespace for the analyses list cache (matches legacy `['analyses']`). */
  all: ['analyses'] as const,

  /**
   * List query key. Aliases {@link ANALYSIS_QUERY_KEYS.all} — the list endpoint
   * takes no params today, so the list key is the namespace itself. Kept as a
   * function for parity with other entities and to leave room for future
   * filters without churning call sites.
   */
  lists: () => ANALYSIS_QUERY_KEYS.all,

  /** Single analysis by id (matches legacy `['analysis', id]`). */
  detail: (id: string) => ['analysis', id] as const,

  /**
   * Deep-link resolution of an analysis id to its owning workspace/org
   * (`GET /analysis/:id/resolve-workspace`). Extends the detail tuple so a detail
   * invalidation also re-resolves.
   */
  resolution: (id: string) => [...ANALYSIS_QUERY_KEYS.detail(id), 'resolution'] as const,

  /**
   * Statista datasets behind the Category & Competition figures
   * (`GET /analysis/:id/statista-datasets`). Extends the detail tuple so a
   * detail invalidation also refetches them.
   */
  statistaDatasets: (id: string) =>
    [...ANALYSIS_QUERY_KEYS.detail(id), 'statista-datasets'] as const,
} as const;
